import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ArrowLeftIcon, DocumentTextIcon, PlusIcon } from '@heroicons/react/24/outline';
import ConfirmationModal from '../../components/ConfirmationModal.tsx';
import FrotaVehicleTabs from '../../components/frota/FrotaVehicleTabs.tsx';
import api from '../../services/api.ts';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { formatPlate } from '../../utils/licensePlate.ts';
import {
  docStatusChipClass,
  docStatusLabel,
  docTypeLabel,
  formatDocDate,
  getFileAbsoluteUrl,
} from '../../utils/fleetDocuments.ts';

const alertBanner = (level: string) => {
  if (level === 'expired') return 'bg-red-50 border-red-200 text-red-900';
  if (level === 'expiring') return 'bg-amber-50 border-amber-200 text-amber-900';
  return 'bg-green-50 border-green-200 text-green-900';
};

const FrotaDocumentos: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: v, isLoading, error } = useQuery(
    ['fleet-vehicle', id],
    async () => (await api.get(`/patrimonio/frota/${id}`)).data,
    { enabled: !!id }
  );

  const deleteMutation = useMutation(
    async (docId: string) => {
      await api.delete(`/patrimonio/frota/${id}/documentos/${docId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['fleet-vehicle', id]);
        queryClient.invalidateQueries(['fleet']);
        setDeleteId(null);
      },
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (error || !v) {
    return <div className="text-center text-red-500">Veículo da frota não encontrado.</div>;
  }

  const docs: any[] = v.documents || [];
  const canWrite = hasPermission('assets.create') || hasPermission('assets.update');
  const canDelete = hasPermission('assets.delete') || hasPermission('assets.update');
  const warningAlerts = (v.alerts || []).filter(
    (a: any) => a.level === 'expiring' || a.level === 'expired'
  );

  const openFile = (doc: any) => {
    if (!doc.fileUrl) return;
    window.open(getFileAbsoluteUrl(doc.fileUrl), '_blank', 'noopener,noreferrer');
  };

  const docSubtitle = (d: any) => {
    const parts: string[] = [];
    if (d.policyNumber) parts.push(`Apólice ${d.policyNumber}`);
    if (d.fileUrl) parts.push(d.fileName ? d.fileName : 'PDF anexado');
    else parts.push('Sem arquivo');
    return parts.join(' · ');
  };

  const addButton = canWrite ? (
    <button
      type="button"
      onClick={() => navigate(`/patrimonio/frota/${id}/documentos/novo`)}
      className="w-full sm:w-auto bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-xl font-medium inline-flex items-center justify-center"
    >
      <PlusIcon className="h-5 w-5 mr-2" />
      Adicionar documento
    </button>
  ) : null;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <p className="text-sm text-gray-500">
            Patrimônio / Frota / {formatPlate(v.licensePlate)} / Documentos
          </p>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center mt-1">
            <DocumentTextIcon className="h-8 w-8 mr-3 text-primary-600" />
            {v.make} {v.model}
          </h1>
        </div>
        <div className="hidden sm:block">{addButton}</div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate('/patrimonio/frota')}
          className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-200"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Voltar à frota
        </button>
      </div>

      <div className="sm:hidden">{addButton}</div>

      {id && <FrotaVehicleTabs id={id} />}

      {warningAlerts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {warningAlerts.map((a: any) => (
            <div key={a.id} className={`rounded-xl border px-4 py-3 text-sm ${alertBanner(a.level)}`}>
              <strong className="block">{a.title}</strong>
              <span className="opacity-80">{a.detail}</span>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-200">
        <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center gap-3">
          <h2 className="font-semibold text-gray-900">Documentos e seguros</h2>
          <span className="text-xs text-gray-500">
            {docs.length} registro{docs.length === 1 ? '' : 's'}
            {docs.length > 0 ? ' · alertas de vencimento' : ''}
          </span>
        </div>

        {docs.length === 0 ? (
          <div className="px-5 py-14 text-center">
            <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-gray-100 flex items-center justify-center">
              <DocumentTextIcon className="h-7 w-7 text-gray-400" />
            </div>
            <p className="text-base font-semibold text-gray-900">Nenhum documento cadastrado</p>
            <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
              Adicione seguro, IPVA, licenciamento, CRLV ou outro documento para acompanhar
              vencimentos e manter os arquivos neste veículo.
            </p>
            {canWrite && (
              <button
                type="button"
                onClick={() => navigate(`/patrimonio/frota/${id}/documentos/novo`)}
                className="mt-5 bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-xl font-medium inline-flex items-center"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Adicionar documento
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="text-left px-5 py-2 font-medium">Documento</th>
                    <th className="text-left px-5 py-2 font-medium">Tipo</th>
                    <th className="text-left px-5 py-2 font-medium">Vencimento</th>
                    <th className="text-left px-5 py-2 font-medium">Status</th>
                    <th className="text-left px-5 py-2 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {docs.map(d => (
                    <tr key={d.id}>
                      <td className="px-5 py-3">
                        <p className="font-medium text-gray-900">{d.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{docSubtitle(d)}</p>
                      </td>
                      <td className="px-5 py-3">
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                          {d.docTypeLabel || docTypeLabel(d.docType)}
                        </span>
                      </td>
                      <td className="px-5 py-3 font-mono">{formatDocDate(d.expiresAt)}</td>
                      <td className="px-5 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${docStatusChipClass(
                            d.status
                          )}`}
                        >
                          {docStatusLabel(d.status)}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            disabled={!d.fileUrl}
                            onClick={() => openFile(d)}
                            className={`text-sm font-medium ${
                              d.fileUrl
                                ? 'text-primary-600 hover:text-primary-800'
                                : 'text-gray-300 cursor-not-allowed'
                            }`}
                          >
                            Abrir
                          </button>
                          {canWrite && (
                            <Link
                              to={`/patrimonio/frota/${id}/documentos/${d.id}/editar`}
                              className="text-sm font-medium text-primary-600 hover:text-primary-800"
                            >
                              Editar
                            </Link>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              onClick={() => setDeleteId(d.id)}
                              className="text-sm font-medium text-red-600 hover:text-red-800"
                            >
                              Excluir
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {docs.map(d => (
                <div key={d.id} className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-semibold text-gray-900">{d.name}</p>
                    <span
                      className={`shrink-0 inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${docStatusChipClass(
                        d.status
                      )}`}
                    >
                      {docStatusLabel(d.status)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    <span className="inline-flex px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-700">
                      {d.docTypeLabel || docTypeLabel(d.docType)}
                    </span>
                    {d.policyNumber && <span>· Apólice {d.policyNumber}</span>}
                  </div>
                  <p className="text-xs text-gray-500">
                    Vencimento {formatDocDate(d.expiresAt)}
                    {d.fileUrl ? ' · arquivo anexado' : ' · Sem arquivo'}
                  </p>
                  <div className="flex items-center gap-4 pt-1">
                    <button
                      type="button"
                      disabled={!d.fileUrl}
                      onClick={() => openFile(d)}
                      className={`text-sm font-medium ${
                        d.fileUrl ? 'text-primary-600' : 'text-gray-300 cursor-not-allowed'
                      }`}
                    >
                      Abrir
                    </button>
                    {canWrite && (
                      <Link
                        to={`/patrimonio/frota/${id}/documentos/${d.id}/editar`}
                        className="text-sm font-medium text-primary-600"
                      >
                        Editar
                      </Link>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => setDeleteId(d.id)}
                        className="text-sm font-medium text-red-600"
                      >
                        Excluir
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <ConfirmationModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Excluir documento"
        message="Tem certeza que deseja excluir este documento? Esta ação não pode ser desfeita."
        onConfirm={() => {
          if (deleteId) deleteMutation.mutate(deleteId);
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};

export default FrotaDocumentos;
